import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash.get';

import { TemplateHeader } from '../../../../../components/template/templateHeader';
import SectionDetails from '../../../../../components/vipProgram/SectionDetails';
import CurrencyHelper from '../../../../../helpers/currencyHelper';

import {
  fetchAccountDonationsTotal,
  fetchAccountEventAmbassadorEventNames,
  fetchAccountFundraisingTotal
} from '../../api/applicantHistoryApi';

const i18nKey = 'applications.template.page.partOne.applicant-history';

interface SectionDetailsField {
  label: React.ReactNode;
  value: React.ReactNode;
  required?: boolean;
}

// SectionDetails is an untyped .jsx component; alias it with the field shape we pass.
const SectionDetailsTyped = SectionDetails as React.FC<{
  fields: SectionDetailsField[];
}>;

interface ApplicantHistoryProps {
  accountId?: string;
  organizationId?: string | number;
  currency?: string;
}

const EMPTY_VALUE = '-';

/**
 * "Applicant history" box on the view application screen. Only rendered for
 * system-admin and organisation-admin credentials. Once the screen has rendered,
 * loads three figures for the applicant's account, all scoped to this charity
 * (organization) and to Grassrootz platform data:
 *   - Fundraising total (sum of raisedAmount across their pages)
 *   - Lifetime donations (sum of amountConverted across their donations)
 *   - Previous applications (de-duplicated events they applied to as an ambassador)
 *
 * Both accountId and organizationId are required; the calls are not fired until
 * both are available.
 */
export const ApplicantHistory: React.FC<ApplicantHistoryProps> = ({
  accountId,
  organizationId,
  currency
}) => {
  const isSystemAdmin = useSelector((state: any) =>
    get(state, 'session.isSystemAdmin')
  );
  const isOrganizationAdmin = useSelector((state: any) =>
    get(state, 'session.isOrganizationAdmin')
  );
  const canView = Boolean(isSystemAdmin || isOrganizationAdmin);

  const [isLoading, setIsLoading] = useState(true);
  const [fundraisingTotal, setFundraisingTotal] = useState<number | null>(null);
  const [donationsTotal, setDonationsTotal] = useState<number | null>(null);
  const [eventNames, setEventNames] = useState<string[] | null>(null);

  useEffect(() => {
    // Clear any previous applicant's data before the next load or early return,
    // so nothing stale lingers when ids change or a request rejects.
    setFundraisingTotal(null);
    setDonationsTotal(null);
    setEventNames(null);

    if (!canView || !accountId || !organizationId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    Promise.allSettled([
      fetchAccountFundraisingTotal(accountId, organizationId),
      fetchAccountDonationsTotal(accountId, organizationId),
      fetchAccountEventAmbassadorEventNames(accountId, organizationId)
    ]).then(([fundraising, donations, events]) => {
      if (cancelled) {
        return;
      }

      if (fundraising.status === 'fulfilled') {
        setFundraisingTotal(fundraising.value);
      }
      if (donations.status === 'fulfilled') {
        setDonationsTotal(donations.value);
      }
      if (events.status === 'fulfilled') {
        setEventNames(events.value);
      }

      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [canView, accountId, organizationId]);

  if (!canView) {
    return null;
  }

  const loader = <Loader active inline size="tiny" />;

  const formatAmount = (value: number | null) =>
    value === null
      ? EMPTY_VALUE
      : CurrencyHelper.formatCurrency(value, currency || undefined);

  const formatEvents = (names: string[] | null): React.ReactNode => {
    if (names === null) {
      return EMPTY_VALUE;
    }
    if (!names.length) {
      return I18n.t(`${i18nKey}.none`);
    }
    return (
      <ul className="applicant-history-events" style={{ margin: 0, paddingLeft: '1.25em' }}>
        {names.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <TemplateHeader as="h5" label={`${i18nKey}.title`} />

      <SectionDetailsTyped
        fields={[
          {
            label: I18n.t(`${i18nKey}.lifetime-fundraising`),
            value: isLoading ? loader : formatAmount(fundraisingTotal),
            required: false
          },
          {
            label: I18n.t(`${i18nKey}.total-donations`),
            value: isLoading ? loader : formatAmount(donationsTotal),
            required: false
          },
          {
            label: I18n.t(`${i18nKey}.event-ambassador-events`),
            value: isLoading ? loader : formatEvents(eventNames),
            required: false
          }
        ]}
      />

      <p
        className="applicant-history-disclaimer"
        style={{ marginTop: '0.5rem', fontSize: '0.85em', opacity: 0.7 }}
      >
        {I18n.t(`${i18nKey}.disclaimer`)}
      </p>
    </>
  );
};

export default ApplicantHistory;
